import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../utils/api';
import './Memory.css';

function Memory() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [embeddingsStatus, setEmbeddingsStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState({});
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setError(null);
      const [configData, statsData] = await Promise.all([
        adminAPI.getMemoryConfig(),
        adminAPI.getMemoryStats()
      ]);
      setConfig(configData);
      setStats(statsData);

      // Fetch embeddings status (don't fail if endpoint not available)
      try {
        const embeddingsData = await adminAPI.getEmbeddingsStatus();
        setEmbeddingsStatus(embeddingsData);
      } catch (e) {
        // console.log('Embeddings status not available:', e.message);  // Debug - commented for prod
      }
    } catch (err) {
      // console.error('Failed to fetch memory data:', err);  // Debug - commented for prod
      setError(err.response?.data?.detail || 'Failed to load memory settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleToggle = async (key, currentValue) => {
    setUpdating(prev => ({ ...prev, [key]: true }));
    try {
      const newConfig = await adminAPI.updateMemoryConfig({ [key]: !currentValue });
      setConfig(newConfig);
      // Refresh stats after toggle
      const newStats = await adminAPI.getMemoryStats();
      setStats(newStats);
    } catch (err) {
      console.error(`Failed to toggle ${key}:`, err);
      setError(err.response?.data?.detail || `Failed to update ${key}`);
    } finally {
      setUpdating(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleNumberChange = async (key, value) => {
    const numValue = parseInt(value, 10);
    if (isNaN(numValue) || numValue < 0) return;

    setUpdating(prev => ({ ...prev, [key]: true }));
    try {
      const newConfig = await adminAPI.updateMemoryConfig({ [key]: numValue });
      setConfig(newConfig);
    } catch (err) {
      console.error(`Failed to update ${key}:`, err);
      setError(err.response?.data?.detail || `Failed to update ${key}`);
    } finally {
      setUpdating(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleStringChange = async (key, value) => {
    setUpdating(prev => ({ ...prev, [key]: true }));
    try {
      const newConfig = await adminAPI.updateMemoryConfig({ [key]: value });
      setConfig(newConfig);
    } catch (err) {
      console.error(`Failed to update ${key}:`, err);
      setError(err.response?.data?.detail || `Failed to update ${key}`);
    } finally {
      setUpdating(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleFloatChange = async (key, value) => {
    const numValue = parseFloat(value);
    if (isNaN(numValue) || numValue < 0 || numValue > 1) return;

    setUpdating(prev => ({ ...prev, [key]: true }));
    try {
      const newConfig = await adminAPI.updateMemoryConfig({ [key]: numValue });
      setConfig(newConfig);
    } catch (err) {
      console.error(`Failed to update ${key}:`, err);
      setError(err.response?.data?.detail || `Failed to update ${key}`);
    } finally {
      setUpdating(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleCleanup = async () => {
    setUpdating(prev => ({ ...prev, cleanup: true }));
    try {
      const result = await adminAPI.cleanupExpiredMemories();
      setSuccessMessage(`Cleaned up ${result.cleaned_up} expired memories`);
      // Refresh stats
      const newStats = await adminAPI.getMemoryStats();
      setStats(newStats);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Failed to cleanup:', err);
      setError(err.response?.data?.detail || 'Failed to cleanup expired memories');
    } finally {
      setUpdating(prev => ({ ...prev, cleanup: false }));
    }
  };

  const handleGenerateEmbeddings = async (force = false) => {
    setUpdating(prev => ({ ...prev, embeddings: true }));
    try {
      const result = await adminAPI.generateAllEmbeddings(force);
      setSuccessMessage(
        `Generated ${result.total_embeddings_generated} embeddings for ${result.users_processed} users`
      );
      // Refresh embeddings status
      const newStatus = await adminAPI.getEmbeddingsStatus();
      setEmbeddingsStatus(newStatus);
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      console.error('Failed to generate embeddings:', err);
      setError(err.response?.data?.detail || 'Failed to generate embeddings');
    } finally {
      setUpdating(prev => ({ ...prev, embeddings: false }));
    }
  };

  if (loading) {
    return (
      <div className="memory">
        <div className="page-header">
          <h1 className="page-title">Memory Management</h1>
        </div>
        <div className="loading-container">
          <div className="spinner"></div>
          <span>Loading memory settings...</span>
        </div>
      </div>
    );
  }

  const toggleItems = [
    {
      key: 'memory_enabled',
      label: 'Master Toggle',
      description: 'Enable or disable all memory management features. When off, zero resources are consumed.',
      icon: PowerIcon,
      isPrimary: true
    },
    {
      key: 'context_enabled',
      label: 'Context Injection',
      description: 'Inject relevant memories and context into LLM queries for personalized responses.',
      icon: BrainIcon,
      disabled: !config?.memory_enabled
    },
    {
      key: 'summary_enabled',
      label: 'Conversation Summaries',
      description: 'Generate rolling summaries of conversations for efficient context management.',
      icon: DocumentIcon,
      disabled: !config?.memory_enabled
    }
  ];

  return (
    <div className="memory">
      <div className="page-header">
        <h1 className="page-title">Memory Management</h1>
        <p className="page-subtitle">
          Configure user memory, context injection, and conversation summary features.
          Disabling memory management eliminates all related resource consumption.
        </p>
      </div>

      {error && (
        <div className="error-banner">
          <ErrorIcon className="error-icon" />
          <span>{error}</span>
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {successMessage && (
        <div className="success-banner">
          <CheckIcon className="success-icon" />
          <span>{successMessage}</span>
        </div>
      )}

      {/* Statistics Overview */}
      {stats && (
        <div className="stats-grid">
          <div className={`stat-card ${config?.memory_enabled ? 'active' : ''}`}>
            <div className="stat-icon">
              <BrainIcon className="icon" />
            </div>
            <div className="stat-info">
              <span className="stat-value">{stats.total_memories}</span>
              <span className="stat-label">Total Memories</span>
            </div>
          </div>
          <div className={`stat-card ${config?.memory_enabled ? 'active' : ''}`}>
            <div className="stat-icon">
              <UsersIcon className="icon" />
            </div>
            <div className="stat-info">
              <span className="stat-value">{stats.total_users_with_memories}</span>
              <span className="stat-label">Users with Memories</span>
            </div>
          </div>
          <div
            className={`stat-card clickable ${config?.summary_enabled ? 'active' : ''}`}
            onClick={() => navigate('/conversation-summaries')}
            title="Click to view conversation summaries"
          >
            <div className="stat-icon">
              <DocumentIcon className="icon" />
            </div>
            <div className="stat-info">
              <span className="stat-value">{stats.total_summaries}</span>
              <span className="stat-label">Conversation Summaries</span>
            </div>
            <ChevronRightIcon className="nav-arrow" />
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <TokenIcon className="icon" />
            </div>
            <div className="stat-info">
              <span className="stat-value">{config?.max_total_context_tokens || 0}</span>
              <span className="stat-label">Max Context Tokens</span>
            </div>
          </div>
        </div>
      )}

      {/* Feature Toggles */}
      <div className="settings-section">
        <h2>Feature Toggles</h2>
        <div className="settings-list">
          {toggleItems.map(({ key, label, description, icon: Icon, isPrimary, disabled }) => (
            <div
              key={key}
              className={`setting-item ${isPrimary ? 'primary' : ''} ${disabled ? 'disabled' : ''}`}
            >
              <div className="setting-icon">
                <Icon className="icon" />
              </div>
              <div className="setting-content">
                <div className="setting-header">
                  <span className="setting-label">{label}</span>
                  {disabled && <span className="disabled-badge">Requires Master Toggle</span>}
                </div>
                <p className="setting-description">{description}</p>
              </div>
              <div className="setting-toggle">
                <label className={`toggle-switch ${updating[key] ? 'updating' : ''}`}>
                  <input
                    type="checkbox"
                    checked={config?.[key] || false}
                    onChange={() => handleToggle(key, config?.[key])}
                    disabled={disabled || updating[key]}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Memory Limits */}
      <div className="settings-section">
        <h2>Memory Limits</h2>
        <div className="config-form">
          <div className="form-row">
            <div className="form-group">
              <label>Max Memories Per User</label>
              <input
                type="number"
                value={config?.max_memories_per_user || 100}
                onChange={(e) => handleNumberChange('max_memories_per_user', e.target.value)}
                disabled={!config?.memory_enabled || updating.max_memories_per_user}
                min="1"
                max="1000"
              />
              <span className="form-hint">Oldest memories are deactivated when limit is reached</span>
            </div>
            <div className="form-group">
              <label>Inferred Memory Expiry (days)</label>
              <input
                type="number"
                value={config?.expiry_days_inferred || 90}
                onChange={(e) => handleNumberChange('expiry_days_inferred', e.target.value)}
                disabled={!config?.memory_enabled || updating.expiry_days_inferred}
                min="0"
                max="365"
              />
              <span className="form-hint">0 = never expire</span>
            </div>
            <div className="form-group">
              <label>Explicit Memory Expiry (days)</label>
              <input
                type="number"
                value={config?.expiry_days_explicit || 0}
                onChange={(e) => handleNumberChange('expiry_days_explicit', e.target.value)}
                disabled={!config?.memory_enabled || updating.expiry_days_explicit}
                min="0"
                max="365"
              />
              <span className="form-hint">0 = never expire</span>
            </div>
          </div>
        </div>
      </div>

      {/* Context Settings */}
      <div className="settings-section">
        <h2>Context Settings</h2>
        <div className="config-form">
          <div className="form-row">
            <div className="form-group">
              <label>Max Memory Context Tokens</label>
              <input
                type="number"
                value={config?.max_context_tokens || 200}
                onChange={(e) => handleNumberChange('max_context_tokens', e.target.value)}
                disabled={!config?.context_enabled || updating.max_context_tokens}
                min="50"
                max="2000"
              />
              <span className="form-hint">Token budget for memory context</span>
            </div>
            <div className="form-group">
              <label>Max Total Context Tokens</label>
              <input
                type="number"
                value={config?.max_total_context_tokens || 500}
                onChange={(e) => handleNumberChange('max_total_context_tokens', e.target.value)}
                disabled={!config?.context_enabled || updating.max_total_context_tokens}
                min="100"
                max="4000"
              />
              <span className="form-hint">Total budget for all context (memory + summary)</span>
            </div>
            <div className="form-group">
              <label>Context Scope</label>
              <select
                value={config?.context_scope || 'per_conversation'}
                onChange={(e) => handleStringChange('context_scope', e.target.value)}
                disabled={!config?.context_enabled || updating.context_scope}
              >
                <option value="per_conversation">Per Conversation</option>
                <option value="per_user">Per User</option>
                <option value="global">Global</option>
              </select>
              <span className="form-hint">Scope of context retrieval</span>
            </div>
          </div>
        </div>
      </div>

      {/* Diverse Context (Consensus Drift Prevention) */}
      <div className="settings-section">
        <h2>Diverse Context</h2>
        <p className="section-description">
          Prevent consensus drift by giving each model different context emphasis in council mode.
          This ensures diverse responses even in extended conversations.
        </p>
        <div className="config-form">
          <div className="form-row">
            <div className="form-group toggle-group">
              <label>Enable Diverse Context</label>
              <label className={`toggle-switch ${updating.diverse_context_enabled ? 'updating' : ''}`}>
                <input
                  type="checkbox"
                  checked={config?.diverse_context_enabled || false}
                  onChange={() => handleToggle('diverse_context_enabled', config?.diverse_context_enabled)}
                  disabled={updating.diverse_context_enabled}
                />
                <span className="toggle-slider"></span>
              </label>
              <span className="form-hint">Each model gets different context emphasis (memory-focused, recent-focused, fresh perspective)</span>
            </div>
            <div className="form-group">
              <label>Consensus Threshold</label>
              <input
                type="number"
                value={config?.consensus_threshold || 0.85}
                onChange={(e) => handleFloatChange('consensus_threshold', e.target.value)}
                disabled={updating.consensus_threshold}
                min="0"
                max="1"
                step="0.05"
              />
              <span className="form-hint">Similarity threshold (0.0-1.0) for "high consensus" alert</span>
            </div>
            <div className="form-group toggle-group">
              <label>Show Divergence Indicator</label>
              <label className={`toggle-switch ${updating.show_divergence_indicator ? 'updating' : ''}`}>
                <input
                  type="checkbox"
                  checked={config?.show_divergence_indicator || false}
                  onChange={() => handleToggle('show_divergence_indicator', config?.show_divergence_indicator)}
                  disabled={updating.show_divergence_indicator}
                />
                <span className="toggle-slider"></span>
              </label>
              <span className="form-hint">Display diversity percentage in synthesis header</span>
            </div>
          </div>
        </div>
        <div className="diverse-context-info">
          <DiversityIcon className="icon" />
          <div className="info-content">
            <strong>How it works:</strong> In council mode, each model receives a different context strategy:
            <ul>
              <li><strong>Memory Focused:</strong> Heavy user memory, minimal recent context</li>
              <li><strong>Recent Focused:</strong> Light memory, heavy conversation context</li>
              <li><strong>Fresh Perspective:</strong> No memory injection (unbiased opinion)</li>
              <li><strong>Historical:</strong> Older memories, conversation summary</li>
              <li><strong>Balanced:</strong> Equal weight (default behavior)</li>
            </ul>
          </div>
        </div>
      </div>

      {/* History RAG Settings */}
      <div className="settings-section">
        <h2>Conversation History (RAG)</h2>
        <p className="section-description">
          Configure how conversation history is retrieved for LLM context.
          Uses hybrid retrieval: Last N recent messages + semantic search for older relevant messages.
        </p>
        <div className="config-form">
          <div className="form-row">
            <div className="form-group toggle-group">
              <label>Enable RAG History</label>
              <label className={`toggle-switch ${updating.rag_history_enabled ? 'updating' : ''}`}>
                <input
                  type="checkbox"
                  checked={config?.rag_history_enabled ?? true}
                  onChange={() => handleToggle('rag_history_enabled', config?.rag_history_enabled)}
                  disabled={updating.rag_history_enabled}
                />
                <span className="toggle-slider"></span>
              </label>
              <span className="form-hint">When off, only last N messages are included (no semantic search)</span>
            </div>
            <div className="form-group">
              <label>Last N Messages (Always Included)</label>
              <input
                type="number"
                value={config?.rag_history_last_n ?? 5}
                onChange={(e) => handleNumberChange('rag_history_last_n', e.target.value)}
                disabled={updating.rag_history_last_n}
                min="1"
                max="20"
              />
              <span className="form-hint">Recent messages always included (catches pronouns, "earlier", etc.)</span>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>RAG Top K</label>
              <input
                type="number"
                value={config?.rag_history_top_k ?? 5}
                onChange={(e) => handleNumberChange('rag_history_top_k', e.target.value)}
                disabled={!config?.rag_history_enabled || updating.rag_history_top_k}
                min="0"
                max="20"
              />
              <span className="form-hint">Max semantically relevant older messages to retrieve</span>
            </div>
            <div className="form-group">
              <label>Similarity Threshold</label>
              <input
                type="number"
                value={config?.rag_history_similarity_threshold ?? 0.3}
                onChange={(e) => handleFloatChange('rag_history_similarity_threshold', e.target.value)}
                disabled={!config?.rag_history_enabled || updating.rag_history_similarity_threshold}
                min="0"
                max="1"
                step="0.05"
              />
              <span className="form-hint">Minimum similarity (0.0-1.0) for RAG retrieval</span>
            </div>
          </div>
        </div>
        <div className="diverse-context-info">
          <HistoryIcon className="icon" />
          <div className="info-content">
            <strong>How it works:</strong> Hybrid retrieval ensures LLMs remember conversation context:
            <ul>
              <li><strong>Last N:</strong> Always includes recent messages for continuity</li>
              <li><strong>RAG Top K:</strong> Finds semantically relevant older messages</li>
              <li><strong>Token Budget:</strong> ~250 tokens per message (Last 5 + RAG 5 = ~2500 tokens)</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Prompted Summary Settings */}
      <div className="settings-section">
        <h2>Prompted Summary Settings</h2>
        <p className="section-description">
          Configure LLM-based conversation summarization for improved context management.
          Summaries are generated after the configured number of turns.
        </p>

        <div className="config-form">
          <div className="form-row">
            <div className="form-group toggle-group">
              <label>Enable Prompted Summary</label>
              <label className={`toggle-switch ${updating.prompted_summary_enabled ? 'updating' : ''}`}>
                <input
                  type="checkbox"
                  checked={config?.prompted_summary_enabled ?? true}
                  onChange={() => handleToggle('prompted_summary_enabled', config?.prompted_summary_enabled)}
                  disabled={updating.prompted_summary_enabled || !config?.memory_enabled}
                />
                <span className="toggle-slider"></span>
              </label>
              <span className="form-hint">Use LLM to generate structured conversation summaries</span>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Summary Threshold (turns)</label>
              <input
                type="number"
                value={config?.prompted_summary_threshold ?? 3}
                onChange={(e) => handleNumberChange('prompted_summary_threshold', e.target.value)}
                disabled={!config?.prompted_summary_enabled || updating.prompted_summary_threshold}
                min="1"
                max="50"
              />
              <span className="form-hint">Start summarization after N turns (recommended: 3-5)</span>
            </div>

            <div className="form-group">
              <label>UI Compaction Threshold (tokens)</label>
              <input
                type="number"
                value={config?.ui_compaction_threshold ?? 100000}
                onChange={(e) => handleNumberChange('ui_compaction_threshold', e.target.value)}
                disabled={updating.ui_compaction_threshold}
                min="10000"
                max="500000"
                step="10000"
              />
              <span className="form-hint">Compact UI when conversation exceeds this token count (default: 100K)</span>
            </div>
          </div>
        </div>

        <div className="diverse-context-info">
          <HistoryIcon className="icon" />
          <div className="info-content">
            <strong>How Prompted Summary Works:</strong>
            <ul>
              <li>After threshold turns, an LLM generates a structured summary</li>
              <li>Summary includes: topics, decisions, key facts, user preferences</li>
              <li>Summaries are deduplicated to avoid repetition</li>
              <li>Context sent to LLM: last N messages + summary + RAG results</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Summary Settings */}
      <div className="settings-section">
        <h2>Summary Settings</h2>
        <div className="config-form">
          <div className="form-row">
            <div className="form-group">
              <label>Update Frequency (queries)</label>
              <input
                type="number"
                value={config?.summary_update_frequency || 3}
                onChange={(e) => handleNumberChange('summary_update_frequency', e.target.value)}
                disabled={!config?.summary_enabled || updating.summary_update_frequency}
                min="1"
                max="20"
              />
              <span className="form-hint">Update summary every N queries</span>
            </div>
            <div className="form-group">
              <label>Max Summary Tokens</label>
              <input
                type="number"
                value={config?.summary_max_tokens || 150}
                onChange={(e) => handleNumberChange('summary_max_tokens', e.target.value)}
                disabled={!config?.summary_enabled || updating.summary_max_tokens}
                min="50"
                max="1000"
              />
              <span className="form-hint">Token limit for summary</span>
            </div>
            <div className="form-group">
              <label>Recent Exchanges to Include</label>
              <input
                type="number"
                value={config?.include_recent_exchanges || 3}
                onChange={(e) => handleNumberChange('include_recent_exchanges', e.target.value)}
                disabled={!config?.summary_enabled || updating.include_recent_exchanges}
                min="0"
                max="10"
              />
              <span className="form-hint">Number of recent Q&A pairs in context</span>
            </div>
          </div>
        </div>
      </div>

      {/* Extraction Settings */}
      <div className="settings-section">
        <h2>Extraction Settings</h2>
        <div className="config-form">
          <div className="form-row">
            <div className="form-group">
              <label>Extraction Provider</label>
              <select
                value={config?.extraction_provider || 'groq'}
                onChange={(e) => handleStringChange('extraction_provider', e.target.value)}
                disabled={!config?.memory_enabled || updating.extraction_provider}
              >
                <option value="groq">Groq</option>
                <option value="openai">OpenAI</option>
                <option value="google">Google</option>
                <option value="claude">Claude</option>
              </select>
              <span className="form-hint">LLM provider for metadata extraction</span>
            </div>
            <div className="form-group">
              <label>Extraction Model</label>
              <input
                type="text"
                value={config?.extraction_model || 'llama-3.3-70b-versatile'}
                onChange={(e) => handleStringChange('extraction_model', e.target.value)}
                disabled={!config?.memory_enabled || updating.extraction_model}
              />
              <span className="form-hint">Model used for extracting memory metadata</span>
            </div>
          </div>
        </div>
      </div>

      {/* Semantic Search */}
      <div className="settings-section">
        <h2>Semantic Search</h2>
        <div className="semantic-search-section">
          <div className="semantic-status">
            <div className="semantic-icon">
              <VectorIcon className="icon" />
            </div>
            <div className="semantic-info">
              <div className="semantic-header">
                <span className="semantic-title">Vector Embeddings</span>
                {embeddingsStatus && (
                  <span className={`coverage-badge ${embeddingsStatus.coverage_percent === 100 ? 'complete' : embeddingsStatus.coverage_percent > 0 ? 'partial' : 'none'}`}>
                    {embeddingsStatus.coverage_percent}% Coverage
                  </span>
                )}
              </div>
              <p className="semantic-description">
                Generate vector embeddings for semantic similarity search.
                This enables finding relevant memories based on meaning, not just keywords.
              </p>
              {embeddingsStatus && (
                <div className="embedding-stats">
                  <div className="embedding-stat">
                    <span className="stat-number">{embeddingsStatus.with_embeddings}</span>
                    <span className="stat-text">with embeddings</span>
                  </div>
                  <div className="embedding-stat">
                    <span className="stat-number">{embeddingsStatus.without_embeddings}</span>
                    <span className="stat-text">without embeddings</span>
                  </div>
                  <div className="embedding-stat">
                    <span className="stat-number">{embeddingsStatus.total_active_memories}</span>
                    <span className="stat-text">total active</span>
                  </div>
                </div>
              )}
              {!embeddingsStatus && (
                <div className="embedding-stats">
                  <span className="stat-text">Loading status...</span>
                </div>
              )}
            </div>
          </div>
          <div className="semantic-actions">
            <button
              className="action-btn primary"
              onClick={() => handleGenerateEmbeddings(false)}
              disabled={!config?.memory_enabled || updating.embeddings}
              title="Generate embeddings for memories that don't have them"
            >
              {updating.embeddings ? (
                <>
                  <span className="btn-spinner"></span>
                  Generating...
                </>
              ) : (
                'Generate Missing'
              )}
            </button>
            <button
              className="action-btn secondary"
              onClick={() => handleGenerateEmbeddings(true)}
              disabled={!config?.memory_enabled || updating.embeddings}
              title="Regenerate all embeddings (use if model was updated)"
            >
              Regenerate All
            </button>
          </div>
        </div>
        <div className="semantic-model-info">
          <span className="model-label">Model:</span>
          <span className="model-name">all-MiniLM-L6-v2</span>
          <span className="model-dims">384 dimensions</span>
        </div>
      </div>

      {/* Maintenance Actions */}
      <div className="settings-section">
        <h2>Maintenance</h2>
        <div className="maintenance-actions">
          <div className="action-card">
            <div className="action-info">
              <h3>Cleanup Expired Memories</h3>
              <p>Remove all memories that have passed their expiration date.</p>
            </div>
            <button
              className="action-btn"
              onClick={handleCleanup}
              disabled={!config?.memory_enabled || updating.cleanup}
            >
              {updating.cleanup ? 'Cleaning...' : 'Run Cleanup'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Icons
function PowerIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M18.36 6.64a9 9 0 1 1-12.73 0" />
      <line x1="12" y1="2" x2="12" y2="12" />
    </svg>
  );
}

function BrainIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 4.5a2.5 2.5 0 0 0-4.96-.46 2.5 2.5 0 0 0-1.98 3 2.5 2.5 0 0 0-1.32 4.24 3 3 0 0 0 .34 5.58 2.5 2.5 0 0 0 2.96 3.08A2.5 2.5 0 0 0 12 19.5a2.5 2.5 0 0 0 4.96.44 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24 2.5 2.5 0 0 0-1.98-3A2.5 2.5 0 0 0 12 4.5" />
      <path d="m15.7 10.4-.9.4" />
      <path d="m9.2 13.2-.9.4" />
      <path d="m13.6 15.7-.4-.9" />
      <path d="m10.8 9.2-.4-.9" />
      <path d="m15.7 13.5-.9-.4" />
      <path d="m9.2 10.9-.9-.4" />
      <path d="m10.5 15.7.4-.9" />
      <path d="m13.1 9.2.4-.9" />
    </svg>
  );
}

function DocumentIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14,2 14,8 20,8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <polyline points="10,9 9,9 8,9" />
    </svg>
  );
}

function UsersIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function TokenIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v12" />
      <path d="M6 12h12" />
    </svg>
  );
}

function ErrorIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}

function CheckIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="20,6 9,17 4,12" />
    </svg>
  );
}

function ChevronRightIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

function VectorIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="6" cy="6" r="2" />
      <circle cx="18" cy="6" r="2" />
      <circle cx="6" cy="18" r="2" />
      <circle cx="18" cy="18" r="2" />
      <path d="M6 8v8" />
      <path d="M18 8v8" />
      <path d="M8 6h8" />
      <path d="M8 18h8" />
      <path d="M8 8l8 8" />
      <path d="M16 8l-8 8" />
    </svg>
  );
}

function DiversityIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 2a10 10 0 0 1 0 20" />
      <path d="M12 2a10 10 0 0 0 0 20" />
      <path d="M2 12h20" />
      <path d="M12 2c2.5 2.5 4 6 4 10s-1.5 7.5-4 10" />
      <path d="M12 2c-2.5 2.5-4 6-4 10s1.5 7.5 4 10" />
    </svg>
  );
}

function HistoryIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
      <path d="M12 2v2" />
      <path d="M12 20v2" />
      <path d="M4.93 4.93l1.41 1.41" />
      <path d="M17.66 17.66l1.41 1.41" />
      <path d="M2 12h2" />
      <path d="M20 12h2" />
    </svg>
  );
}

export default Memory;
